import { StrictMode } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import LandingPage from './pages/LandingPage';
import AppPage from './pages/AppPage';
import { defineCustomElements } from './register-webcomponents';
import { UserInfoProvider } from './providers/user-info-provider/user-info-provider';
import { ToastProvider } from './providers/toast-provider/toast-provider';
import { SiteNoticeProvider } from './providers/site-notice-provider/site-notice-provider';
import Layout from './common/components/Layout';
import { initDatadogRum } from './app/datadog';
import RequireAuth from './services/auth/RequireAuth';
import { msalInstance, setupAuthentication } from './app/msal';
import { TermsOfUseGate } from './features/TermsOfUse';
import './index.scss';

await setupAuthentication();

if (import.meta.env.VITE_DATADOG_RUM_ENABLED === 'true') {
  initDatadogRum();
}

defineCustomElements(globalThis.window);

if (import.meta.env.VITE_MSW_ENABLED === 'true') {
  const { browserWorker } = await import('./mocks/browser');
  await (import.meta.env.BASE_URL !== '/' && import.meta.env.BASE_URL
    ? browserWorker.start({
        serviceWorker: {
          url: `${import.meta.env.BASE_URL}mockServiceWorker.js`,
        },
        findWorker: (scriptUrl, mockServiceWorkerUrl) => {
          return scriptUrl.includes(mockServiceWorkerUrl);
        },
        onUnhandledRequest: 'bypass',
      })
    : await browserWorker.start({
        onUnhandledRequest: 'bypass',
      }));
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } },
});

const router = createBrowserRouter(
  [
    {
      element: <LandingPage />,
      path: '/',
    },
    {
      element: (
        <Layout>
          <Outlet />
        </Layout>
      ),
      children: [
        {
          element: (
            <TermsOfUseGate>
              <AppPage />
            </TermsOfUseGate>
          ),
          path: '/app',
        },
      ],
    },
  ],
  {
    basename: import.meta.env.BASE_URL,
  },
);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- #root element should always exist
createRoot(document.querySelector('#root')!).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RequireAuth>
            <UserInfoProvider>
              <SiteNoticeProvider>
                <RouterProvider router={router} />
              </SiteNoticeProvider>
            </UserInfoProvider>
          </RequireAuth>
        </ToastProvider>
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>,
);
